/**
 * "Right now": a trailing window against the same clock window on other days.
 * Unlike the tiles below, which start on the hour, "past 60 min" here is
 * exactly the last 60 minutes each source has. Data from /velocity/compare
 * (backend services/velocity/compare).
 */

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { velocityAPI } from '../../services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '../ui'
import { clockOfHour } from '../../lib/velocity'

const WINDOWS = [
  { minutes: 60, label: 'Past 60 min' },
  { minutes: 180, label: 'Past 3 h' },
  { minutes: 360, label: 'Past 6 h' },
  { minutes: 0, label: 'Today so far' },
]
const REFRESH_MS = 5 * 60 * 1000

const fmt = (value, format) => {
  if (value == null) return '–'
  if (format === 'money') return `$${Math.round(value).toLocaleString()}`
  if (format === 'pct') return `${(value * 100).toFixed(1)}%`
  return value >= 100 || Number.isInteger(value) ? Math.round(value).toLocaleString() : value.toFixed(1)
}
const span = w => `${clockOfHour(w.from / 60)}–${clockOfHour(w.to / 60)}`

/** Now against a baseline, as a signed percentage; a rate shows points. */
function Diff({ now, base, format }) {
  if (now == null || base == null || base === 0) return null
  if (format === 'pct') {
    const pts = (now - base) * 100
    return <span className={pts >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{pts >= 0 ? '+' : ''}{pts.toFixed(1)} pts</span>
  }
  const d = now / base - 1
  return <span className={d >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{d >= 0 ? '+' : ''}{Math.round(d * 100)}%</span>
}

export default function VelocityCompare() {
  const [minutes, setMinutes] = useState(60)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let live = true
    const load = () => {
      setLoading(true)
      velocityAPI.getCompare({ minutes })
        .then(r => { if (live) { setData(r.data.data); setError(null) } })
        .catch(err => { if (live) setError(err.response?.data?.error?.message || err.message) })
        .finally(() => { if (live) setLoading(false) })
    }
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => { live = false; clearInterval(t) }
  }, [minutes])

  const weekday = data?.weekday || 'weekday'
  const columns = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'lastWeek', label: `Last ${weekday}` },
    { key: 'last7', label: 'Avg last 7 days' },
    { key: 'sameWeekday4', label: `Avg last 4 ${weekday}s` },
    { key: 'sameWeekday', label: `Avg all ${weekday}s` },
  ]
  const shown = data?.minutes === (minutes || null) ? data : null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Right now</CardTitle>
            <CardDescription className="text-xs">
              A trailing window, not one starting on the hour, against the same clock window on other days. Each row ends where its data ends: orders are live, BigQuery rows trail by about an hour.
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground mr-1" />}
            {WINDOWS.map(w => (
              <Button key={w.minutes} size="sm" variant={minutes === w.minutes ? 'default' : 'outline'} onClick={() => setMinutes(w.minutes)}>{w.label}</Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!shown && !error && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Comparing…</div>}
        {shown && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left py-1.5 pr-3 font-medium">Metric</th>
                  <th className="text-right py-1.5 px-3 font-medium">Now</th>
                  {columns.map(c => <th key={c.key} className="text-right py-1.5 px-3 font-medium whitespace-nowrap">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {shown.metrics.map(m => (
                  <tr key={m.key} className="border-t">
                    <td className="py-2 pr-3">
                      <div>{m.label}</div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">{span(m.window)}</div>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-base font-semibold">{fmt(m.now, m.format)}</td>
                    {columns.map(c => {
                      const base = m[c.key]
                      return (
                        <td key={c.key} className="py-2 px-3 text-right tabular-nums" title={base?.n > 1 ? `average of ${base.n} days` : undefined}>
                          <div>{fmt(base?.value, m.format)}{base?.n > 1 && <span className="ml-1 text-[10px] text-muted-foreground">×{base.n}</span>}</div>
                          <div className="text-[11px]"><Diff now={m.now} base={base?.value} format={m.format} /></div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Percentages are now against each column. &ldquo;All {weekday}s&rdquo; goes back to the first week of data, so growth pulls it down; the last 4 is the fairer read.
              {shown.ordersSource === 'bigquery' && ' Orders from BigQuery (Manifold unreachable), so no revenue.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
